import { useState } from "react";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

/**
 * Trigger a browser download for a Blob returned by an API call.
 * Exported because several tabs download receipts/PDFs outside this button.
 */
export function downloadBlob(raw, fileName) {
  const blob =
    raw instanceof Blob ? raw : new Blob([raw], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * ExportButton — CSV export with the platform's file-naming convention.
 *
 * The backend already sets Content-Disposition; `fileName` is the fallback used
 * when the browser cannot read it (BRD §4.3: [ModuleName]_[YYYY-MM-DD].csv).
 *
 * Usage:
 *   <ExportButton
 *     moduleName="Rider_Expenses"
 *     onExport={() => exportRiderExpenses(filters)}
 *   />
 */
export default function ExportButton({
  moduleName = "Export",
  onExport,
  disabled = false,
  label = "Export",
  extension = "csv",
  size,
}) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!onExport) return;
    setLoading(true);
    try {
      const raw = await onExport();
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(raw, `${moduleName}_${date}.${extension}`);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || err?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      icon={<DownloadOutlined />}
      onClick={handleClick}
      loading={loading}
      disabled={disabled}
      size={size}
    >
      {label}
    </Button>
  );
}
